import React from 'react';
import { useTranslation } from 'react-i18next';
import { WarnIcon } from './icons/WarnIcon';
import { DashboardOutlined } from '@ant-design/icons';
import { SectionIcon } from './SectionIcon';

const at = point => ({ left: `${point.left.toFixed(2)}%`, top: `${point.top.toFixed(2)}%` });
export const MileageChart = ({ chart }) => {
    const { t } = useTranslation('reportPage');

    return (
        <section id="mileage" className="card" aria-labelledby="mileage-title" style={{ gap: 16 }}>
            <div className="sechead">
                <h2 id="mileage-title" className="h2">
                    <SectionIcon icon={DashboardOutlined} />
                    {t('mileage.title')}
                </h2>
                <div className="legend">
                    <span className="lg">
                        <span className="lg-dot" />
                        {t('mileage.legendRecords', { count: chart.count })}
                    </span>
                    {chart.inspection && (
                        <span className="lg">
                            <span className="lg-dot insp" />
                            {t('layout.legendInspection')}
                        </span>
                    )}
                    {chart.gapLabels.length > 0 && (
                        <span className="lg">
                            <span className="lg-gap" />
                            {t('mileage.legendGaps')}
                        </span>
                    )}
                    {chart.drops.length > 0 && (
                        <span className="lg">
                            <span className="lg-warn" />
                            {t('mileage.legendDrops')}
                        </span>
                    )}
                </div>
            </div>
            {chart.drops.length > 0 && (
                <div className="warnbox" role="note">
                    <WarnIcon color="#D97706" />
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}>{chart.dropTitle}</div>
                        <ul>
                            {chart.drops.map((line, lineIndex) => (
                                <li key={lineIndex}>{line}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            <figure style={{ margin: 0 }} aria-label={chart.aria}>
                <div className="chart">
                    <div className="plot">
                        {chart.grid.map(line => (
                            <React.Fragment key={line.top}>
                                <div className="gridline" style={{ top: `${line.top}%` }} />
                                <span className="ytick" style={{ top: `${line.top}%` }}>
                                    {line.label}
                                </span>
                            </React.Fragment>
                        ))}
                        {chart.xticks.map(tick => (
                            <span className="xtick" style={{ left: `${tick.left.toFixed(2)}%` }} key={tick.label}>
                                {tick.label}
                            </span>
                        ))}
                        <svg
                            viewBox="0 0 1000 1000"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                overflow: 'visible',
                            }}
                        >
                            {chart.area && (
                                <>
                                    <defs>
                                        <linearGradient id="mileage-area" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0" stopColor="#E83E8C" stopOpacity="0.28" />
                                            <stop offset="1" stopColor="#E83E8C" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <path d={chart.area} fill="url(#mileage-area)" stroke="none" />
                                </>
                            )}
                            {chart.gap && (
                                <path
                                    d={chart.gap}
                                    fill="none"
                                    stroke="#9394B0"
                                    strokeWidth="2"
                                    strokeDasharray="5 6"
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                            {chart.solid && (
                                <path
                                    d={chart.solid}
                                    fill="none"
                                    stroke="#E83E8C"
                                    strokeWidth="2.5"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                            {chart.drop && (
                                <path
                                    d={chart.drop}
                                    fill="none"
                                    stroke="#D97706"
                                    strokeWidth="2.5"
                                    strokeDasharray="7 5"
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                        </svg>
                        {chart.gapLabels.map(label => (
                            <span className="glabel" style={at(label)} key={`${label.left}-${label.top}`}>
                                {label.text}
                            </span>
                        ))}
                        {chart.dots.map(dot => (
                            <span
                                className={`cdot${dot.kind ? ` ${dot.kind}` : ''}`}
                                style={at(dot)}
                                title={dot.title}
                                key={`${dot.left}-${dot.top}`}
                            />
                        ))}
                        {chart.labels.map(label => (
                            <span
                                className={`clabel${label.warn ? ' warn' : ''}${label.insp ? ' insp' : ''}`}
                                style={{ ...at(label), ...(label.above ? { transform: 'translate(0,-100%)' } : {}) }}
                                key={`${label.left}-${label.text}`}
                            >
                                {label.text}
                                {label.sub && <span className="sub">{label.sub}</span>}
                            </span>
                        ))}
                    </div>
                </div>
            </figure>
        </section>
    );
};
